import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { http, formatSoles, priceForQty, fileUrl } from "@/api";
import { useStore } from "@/context/StoreContext";
import { Button } from "@/components/ui/button";
import { ShoppingBag, Minus, Plus, Package2, Clock3, Palette, ArrowLeft, Play } from "lucide-react";

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useStore();
  const [product, setProduct] = useState(null);
  const [qty, setQty] = useState(1);
  const [active, setActive] = useState(0);
  const [variant, setVariant] = useState(null);
  const [color, setColor] = useState(null);

  useEffect(() => {
    http.get(`/products/${id}`).then((r) => {
      if (r.data.is_active === false) return navigate("/catalogo");
      setProduct(r.data);
      setActive(0);
      setVariant(r.data.variants?.[0]?.label || null);
      setColor(null);
      setQty(r.data.min_order_qty || 1);
    }).catch(() => navigate("/catalogo"));
  }, [id, navigate]);

  if (!product) return <div className="max-w-7xl mx-auto px-6 py-16">Cargando...</div>;

  const inStock = product.stock_status === "in_stock";
  const unit = priceForQty(product, qty, variant);
  const total = unit * qty;
  const media = [...(product.images || []).map((src) => ({ src: fileUrl(src), video: false })), ...(product.videos || []).map((src) => ({ src: fileUrl(src), video: true }))];
  const current = media[active];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="product-detail-page">
      <button onClick={() => navigate(-1)} className="text-sm text-neutral-500 flex items-center gap-1 mb-6 hover:text-[var(--brand-primary)]">
        <ArrowLeft size={14} /> Volver
      </button>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
        <div>
          <div className="aspect-[4/5] rounded-3xl overflow-hidden bg-white border border-black/5">
            {current ? (
              current.video ? (
                <video src={current.src} controls className="w-full h-full object-cover bg-black" data-testid="product-main-video" />
              ) : (
                <img src={current.src} alt={product.name} className="w-full h-full object-cover" data-testid="product-main-image" />
              )
            ) : (
              <div className="w-full h-full flex items-center justify-center text-neutral-300"><Package2 size={100} /></div>
            )}
          </div>
          {media.length > 1 && (
            <div className="flex gap-3 mt-4 overflow-x-auto scroll-x pb-1" data-testid="product-thumbnails">
              {media.map((m, i) => (
                <button
                  key={m.src + i}
                  onClick={() => setActive(i)}
                  className={`relative h-20 w-20 flex-shrink-0 rounded-xl overflow-hidden border-2 transition-colors ${i === active ? "border-[var(--brand-primary)]" : "border-transparent opacity-70 hover:opacity-100"}`}
                  data-testid={`product-thumb-${i}`}
                >
                  {m.video ? (
                    <div className="w-full h-full bg-neutral-900 flex items-center justify-center text-white"><Play size={20} /></div>
                  ) : (
                    <img src={m.src} alt="" className="w-full h-full object-cover" />
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
        <div>
          <div className="text-xs uppercase tracking-widest text-neutral-400 font-semibold" data-testid="product-code">{product.code}</div>
          <h1 className="font-serif text-4xl sm:text-5xl font-bold mt-2 leading-tight">{product.name}</h1>
          <div className="mt-4 flex flex-wrap gap-2">
            <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider ${inStock ? "badge-stock" : "badge-preorder"}`}>
              {inStock ? "En stock / Entrega inmediata" : "Bajo pedido"}
            </span>
            {!inStock && product.production_time && (
              <span className="px-3 py-1 rounded-full text-xs font-semibold bg-neutral-100 flex items-center gap-1">
                <Clock3 size={12} /> {product.production_time}
              </span>
            )}
            {product.category === "arreglos" && (
              <span className="px-3 py-1 rounded-full text-xs font-semibold bg-[var(--brand-accent)]/10 text-[var(--brand-accent)] flex items-center gap-1">
                <Palette size={12} /> Tonalidad a tu elección
              </span>
            )}
          </div>
          <p className="mt-5 text-neutral-600 leading-relaxed">{product.description}</p>
          {product.features && (
            <ul className="mt-4 space-y-1 text-sm text-neutral-600 list-disc pl-5" data-testid="product-features">
              {product.features.split("\n").filter(Boolean).map((f, i) => <li key={i}>{f}</li>)}
            </ul>
          )}
          {product.contents && (
            <div className="mt-4 p-4 rounded-xl bg-white border border-black/5">
              <div className="text-xs uppercase tracking-widest text-neutral-400 font-semibold">Contenido</div>
              <div className="mt-1">{product.contents}</div>
            </div>
          )}

          <div className="mt-6 flex items-end gap-6">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-neutral-400">Precio actual</div>
              <div className="font-serif text-5xl font-bold text-[var(--brand-primary)]" data-testid="product-price">{formatSoles(unit)}</div>
            </div>
            <div className="text-sm text-neutral-500">
              {unit < product.retail_price ? (
                <span className="flex flex-col">
                  <span className="line-through">{formatSoles(product.retail_price)}</span>
                  <span className="text-[var(--brand-accent)] font-semibold">
                    {product.promo_price && unit === Number(product.promo_price) ? "Precio promocional" : "Precio mayorista aplicado"}
                  </span>
                </span>
              ) : (
                <span>Minorista · {formatSoles(product.retail_price)}</span>
              )}
            </div>
          </div>

          {product.variants?.length > 0 && (
            <div className="mt-5">
              <div className="text-xs uppercase tracking-widest text-neutral-400 font-semibold mb-2">Medida / variante</div>
              <div className="flex gap-2 flex-wrap">
                {product.variants.map((v) => (
                  <button key={v.label} onClick={() => setVariant(v.label)}
                    className={`px-4 py-2 rounded-xl text-sm border transition-colors ${variant === v.label ? "border-[var(--brand-primary)] bg-[var(--brand-primary)]/5 font-semibold" : "border-black/10 bg-white"}`}
                    data-testid={`variant-${v.label}`}>
                    {v.label}{v.price ? ` · ${formatSoles(v.price)}` : ""}
                  </button>
                ))}
              </div>
            </div>
          )}

          {product.colors?.length > 0 && (
            <div className="mt-5">
              <div className="text-xs uppercase tracking-widest text-neutral-400 font-semibold mb-2">Color</div>
              <div className="flex gap-2 flex-wrap">
                {product.colors.map((c) => (
                  <button key={c} onClick={() => setColor(color === c ? null : c)}
                    className={`px-4 py-2 rounded-full text-sm border transition-colors ${color === c ? "border-[var(--brand-accent)] bg-[var(--brand-accent)]/10 font-semibold" : "border-black/10 bg-white"}`}
                    data-testid={`color-${c}`}>
                    {c}
                  </button>
                ))}
              </div>
            </div>
          )}

          {product.wholesale_tiers?.length > 0 && (
            <div className="mt-5">
              <div className="text-xs uppercase tracking-widest text-neutral-400 font-semibold mb-2">Escala mayorista</div>
              <div className="flex gap-2 flex-wrap">
                {product.wholesale_tiers.map((t) => (
                  <div key={t.min_qty} className="px-4 py-2 bg-white border border-black/10 rounded-xl text-sm">
                    <span className="font-semibold">Desde {t.min_qty} u.</span>
                    <span className="ml-2 text-[var(--brand-primary)] font-bold">{formatSoles(t.price)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="mt-8 flex items-center gap-4">
            <div className="flex items-center bg-white border border-black/10 rounded-full">
              <button onClick={() => setQty(Math.max(1, qty - 1))} className="h-12 w-12 flex items-center justify-center" data-testid="product-qty-dec"><Minus size={16} /></button>
              <span className="w-10 text-center font-semibold" data-testid="product-qty">{qty}</span>
              <button onClick={() => setQty(qty + 1)} className="h-12 w-12 flex items-center justify-center" data-testid="product-qty-inc"><Plus size={16} /></button>
            </div>
            <Button
              onClick={() => addToCart(product, qty, { variant, color })}
              className="btn-primary flex-1 h-12 rounded-full font-semibold"
              data-testid="product-add-to-cart"
            >
              <ShoppingBag size={16} className="mr-2" /> Agregar · {formatSoles(total)}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
